import _ from 'lodash'
import { dayjsTz as dayjs } from '~/utils/dayjs'

// 権限レベル定数
export const PERMISSION_LEVEL = {
  NONE: 0,   // なし
  READ: 1,   // 閲覧のみ
  WRITE: 2,  // 編集可
}

// 権限レベル選択肢（ラジオボタン等で使用）
export const PERMISSION_LEVEL_OPTIONS = [
  { text: 'なし', value: PERMISSION_LEVEL.NONE },
  { text: '閲覧のみ', value: PERMISSION_LEVEL.READ },
  { text: '編集可', value: PERMISSION_LEVEL.WRITE },
]

// 権限定義（共通コンポーネントで使用）
export const PERMISSION_GROUPS = [
  {
    title: '店舗向け',
    items: [
      { key: 'can_client', name: '顧客', description: '作成・編集・削除' },
      { key: 'can_reserve', name: '予約', description: '作成・編集・削除' },
      { key: 'can_lesson', name: 'セッション', description: 'データ更新' },
      { key: 'can_log', name: 'ライフログ', description: 'リプライ' },
      { key: 'can_payment', name: '支払い・チケット', description: '実行・キャンセル' },
      { key: 'can_point', name: 'ポイント', description: '付与・行使' },
      { key: 'can_alert', name: 'お知らせ', description: '配信' },
      { key: 'can_settings', name: '店舗設定', description: '店舗・予約・スタッフ・ブースなど' },
      { key: 'can_data', name: '店舗データ分析', description: 'POS・営業データ確認・出力など' },
      { key: 'can_cp_fix', name: '店舗締め処理', description: '店舗の月次締め処理' },
    ],
  },
  {
    title: '事業者共通項目',
    items: [
      { key: 'can_headquarter_settings', name: '全店舗マスタ設定', description: '予約メニュー・チケット・コースなど' },
      { key: 'can_headquarter_data', name: '全店舗データ分析', description: '全店舗を横断した売上/分析データ' },
      { key: 'can_hq_fix', name: '本社締め処理', description: '本社としての月次締め処理' },
      // AI連携の利用（Staff::ATTRS / StaffRole::PERMISSION_KEYS）。READ_LINK_RULES には入れない（他権限から連動しない）
      { key: 'can_ai_integration', name: 'AI連携の利用', description: 'Claude などの AI アプリを自分で接続できる（閲覧以上で可）' },
    ],
  },
]

// 全権限キー
export const PERMISSION_KEYS = PERMISSION_GROUPS.flatMap(g => g.items.map(i => i.key))

// read連動ルール: { 連動先: [連動元の配列] }
// 連動元のいずれかがread以上のとき、連動先に最低readを自動付与する。
// （Staff::READ_LINK_RULES と一致させること。書き込みは連動しない）
export const READ_LINK_RULES = {
  can_lesson:                ['can_client', 'can_reserve', 'can_headquarter_data'],
  can_log:                   ['can_client', 'can_headquarter_data'],
  can_payment:               ['can_client', 'can_headquarter_data'],
  can_point:                 ['can_client', 'can_headquarter_data'],
  can_alert:                 ['can_client', 'can_headquarter_data'],
  can_client:                ['can_headquarter_data'],
  can_data:                  ['can_headquarter_data'],
  can_headquarter_settings:  ['can_headquarter_data'],
}

// 顧客閲覧権限により自動でreadが付与される関連権限（PermissionsTableのヒント表示用）
export const CLIENT_RELATED_READ_KEYS = Object.keys(READ_LINK_RULES).filter(
  k => READ_LINK_RULES[k].includes('can_client')
)

// 連動ルールを適用した実効権限値を返す
export function effectivePermissionLevel(perms, key) {
  if (!perms) return PERMISSION_LEVEL.NONE
  const base = perms[key] || PERMISSION_LEVEL.NONE
  const sources = READ_LINK_RULES[key]
  if (sources && sources.some(src => (perms[src] || 0) >= PERMISSION_LEVEL.READ)) {
    return Math.max(base, PERMISSION_LEVEL.READ)
  }
  return base
}

// 一覧の書き込みの入口（行の「編集」ボタン・見出し帯の「追加」・並び替えの取っ手・名前リンク・⋮ の書き込み系。仕様書 7.1）を出すか。
// can_* が編集可（2）以上のときだけ true。
// key はサーバの CmpPermissionCheck::PERMISSION_MAP で、その画面の API に要る権限
export function canWrite(staff, key) {
  return ((staff && staff[key]) || 0) >= PERMISSION_LEVEL.WRITE
}

// 閲覧だけのスタッフに、書き込みの確定ボタンを「無効にして見せる」ときの理由（title・注記に出す）
export const NO_WRITE_PERMISSION_REASON = '編集権限がないため変更できません'

// canWrite(staff, key) が false のときだけ理由を返す（編集可なら null）。:disabled と :title にそのまま使う
export function writeDisabledReason(staff, key) {
  return canWrite(staff, key) ? null : NO_WRITE_PERMISSION_REASON
}

// 顧客一覧API（floor/clients#index_light, search, show）の許可判定。
// サーバ側 CmpPermissionCheck::CLIENT_BUSINESS_WRITE_KEYS と一致させること。
// 業務系のいずれかでwrite権限を持つ、または can_client がread以上であれば顧客選択UIを表示。
export const CLIENT_BUSINESS_WRITE_KEYS = [
  'can_reserve', 'can_lesson', 'can_payment', 'can_point', 'can_log', 'can_alert',
]

export function canFetchClientList(perms) {
  if (!perms) return false
  if (effectivePermissionLevel(perms, 'can_client') >= PERMISSION_LEVEL.READ) return true
  return CLIENT_BUSINESS_WRITE_KEYS.some(
    k => (perms[k] || 0) >= PERMISSION_LEVEL.WRITE
  )
}

// 権限オブジェクトから「編集可」「閲覧のみ」の項目を抜き出して文字列化
// 例: { can_client: 2, can_reserve: 1, can_lesson: 0, ... } → ["顧客✏️", "予約👁"]
// can_client >= read による連動許可も加味する
// 本部オーナーが値に関係なく「編集」扱いになる権限（サーバ: AiAccess::ScopePolicy.owner? でオーナーは常に AI連携を使える）。
// オーナーの他の権限は保存値が全部「編集」なので、DB の既定 0 のこれだけ「なし」と見えて食い違っていた（2026-10-24）
export const OWNER_ALWAYS_WRITE_KEYS = ['can_ai_integration']

// 表示用の権限値。オーナーなら OWNER_ALWAYS_WRITE_KEYS を「編集」にした複製を返す（元のオブジェクト・DB の値は変えない）。
// オーナー以外は渡された値をそのまま返す
export function permissionsForDisplay(perms, { isOwner = false } = {}) {
  if (!isOwner || !perms) return perms
  const shown = { ...perms }
  OWNER_ALWAYS_WRITE_KEYS.forEach(k => { shown[k] = PERMISSION_LEVEL.WRITE })
  return shown
}

export function summarizePermissions(perms) {
  if (!perms) return { writes: [], reads: [] }
  const writes = []
  const reads = []
  PERMISSION_GROUPS.forEach(g => {
    g.items.forEach(item => {
      const level = effectivePermissionLevel(perms, item.key)
      if (level === PERMISSION_LEVEL.WRITE) writes.push(item.name)
      else if (level === PERMISSION_LEVEL.READ) reads.push(item.name)
    })
  })
  return { writes, reads }
}

export let form_config = {
  headers: {
    'content-type': 'multipart/form-data'
  }
}

export let staff = {
  name: null,
  email: null,
  recieve_reservation_alert: false,
  recieve_log_alert: false,
  can_client: 2,
  can_reserve: 2,
  can_lesson: 2,
  can_log: 2,
  can_payment: 2,
  can_point: 2,
  can_alert: 2,
  can_settings: 2,
  can_data: 2,
  can_edit_datas: true,
  can_headquarter_settings: 2,
  can_headquarter_data: 2,
  can_hq_fix: 2,
  can_cp_fix: 2,
  // AI連携は外部の AI サービスにデータが渡るので、新規スタッフでも既定は使わせない
  can_ai_integration: 0,
  staff_role_id: null,
  able_all_company: true,
  able_company_ids: [],
  calendars: [],
}

export let client = {
  status: 'trial',
  id: null,
  name: null,
  email: null,
  phone_number: null,
}

export let subsc_course_settings = {
  course_id: null,
  name: null,
  ticket_number: 0,
  price: 0,
  next_subsc_ticket_end_style: "now_to_limit",
  expire_days: 0,
  able_menu_ids: [],
  is_subscription: false,
}

export let client_cart = {
  name: null,
  price_sum: 0,
  course_1: _.cloneDeep(subsc_course_settings),
  course_2: _.cloneDeep(subsc_course_settings),
  orders: [],
  discounts: [],
}

export let calendar = {
  id: null,
  original_id: null,
  name: null,
  g_calendar_id: null,
  hpb_staff_uid: null,
  hpb_staff_name: null,
  sort: 1,
  priority: 1,
  color: '#db0000',
  email: null,
  use_smartlock: false,
  smartlock_type: null,
  smartlock_settings: null,
  attendance_setting: {
    can_sunday: true,
    can_monday: true,
    can_thuseday: true,
    can_wedensday: true,
    can_thursday: true,
    can_friday: true,
    can_saturday: true,
    can_holiday: true,
    manage_holiday: false,
    from_on_sunday: null,
    from_on_monday: null,
    from_on_thuseday: null,
    from_on_wedensday: null,
    from_on_thursday: null,
    from_on_friday: null,
    from_on_saturday: null,
    from_on_holiday: null,
    to_on_sunday: null,
    to_on_monday: null,
    to_on_thuseday: null,
    to_on_wedensday: null,
    to_on_thursday: null,
    to_on_friday: null,
    to_on_saturday: null,
    to_on_holiday: null
  },
}

export let resource = {
  id: null,
  name: null,
  sort: 1,
  priority: 1,
  color: '#db0000'
}

export let lesson = {
  id: null,
  name: null,
  g_calendar_id: null
}

export let client_trend = {
  date: null,
  weight: null,
  fat_rate: null,
  muscle_weight: null,
  fat_weight: null,
  water_weight: null,
  sleep_from: null,
  sleep_to: null,
  sleep_hour: null,
  condition: null,
  alcohol: null,
  breakfast_memo: null,
  lunch_memo: null,
  dinner_memo: null,
  other_memo: null,
  breakfast_reply: null,
  lunch_reply: null,
  dinner_reply: null,
  other_reply: null,
  summary_reply: null,
  client_rating: null,
  daialy: null,
  data: null,
  manage_food: null,
  no_alcohol: null,
  do_walkiing: null,
  do_toilet: null,
  food_doc_ids: [],
  chats: [],
}

export let cl_payment = {
  id: null,
  payment_type: 'item',
  name: null,
  price_subtotal: 0,
  spend_point: 0,
  disacount_price_sum: 0,
  discount_amount: 0,
  price_sum: 0,
  tax_sum: 0,
  normal_tax_sum: 0,
  keigen_tax_sum: 0,
  azukari_price: 0,
  otsuri_price: 0,

  // ticket
  description: null,
  ticket_number: 1,
  stock_number: 1,
  use_from: dayjs().format('YYYY-MM-DD'),
  use_to: dayjs().add(60, 'd').format('YYYY-MM-DD'),
  expire_days: 60,
  able_menu_ids: []
}

export let point = {
  id: null,
  title: null,
  price: 0,
  use_type: null,
  limit_date: null
}

export let menu = {
  company_group_id: null,
  company_ids: [],
  reserve_setting_id: null,
  survey_id: null,
  sort: 1,
  name: '',
  name_inner: '',
  description: '',
  show_client: true,
  reserve_type: 'calendar', // Phase 2追加: calendar / event
  reserve_min: 60,
  lesson_min: 60,
  calender_unit_handle_edit: false,
  calender_unit: 60,
  calender_unit_lists: '',
  calender_unit_ojb: {
    sunday: '',
    monday: '',
    thuseday: '',
    wedensday: '',
    thursday: '',
    friday: '',
    saturday: '',
    holiday: ''
  },
  max_seat_size: 1,
  need_ticket_size: 1,
  pay_as_you_go: false,
  allow_ticket: true,
  allow_pay_as_you_go: false,
  can_pay_online: false,
  can_pay_onshop: true,
  price: 0,
  restrict_calendar: false,
  restrict_resource: false,
  able_calendar_ids: [],
  able_resource_ids: [],
  option_settings: {
    use_menu_option: false,
    use_calendar_option: false,
    can_pay_on_shop: false,
    menu_option_price: 0,
    menu_options: [],
    calendar_options: []
  },
  select_calendar: true,
  able_calendar_settings: [],
  count_cancel: true,
  allow_smartlock_open: false,
  allow_smartlock_open_entrance: true,
  max_reserve_size: null,
  max_reserve_size_by_day: null,
  tentative_reservation_calendar_ids: [],
  auto_confirm_tentative_reservation: false,
  today_only_auto_confirm_calendar: false,
  // Phase 2追加: イベント用設定
  event_seat_limit: 10,
  event_trial_seat_limit: 0,
  can_cancel_wait: true,
  check_attendance: false,
  show_trainer_to_client: true,
  show_seat_count_to_client: true,
  // 予約対象期間
  reservable_from: null,
  reservable_to: null,
}

export let course = {
  company_group_id: null,
  company_ids: [],
  use_new_version: true,
  sort: 1,
  time_zone_id: null,
  base_menu_id: null,
  reserve_setting_id: null,
  name: '',
  name_inner: "",
  description: '',
  price: 0,
  // 月契約系のチケットルールでは入力欄が非表示のまま値だけDBに残るため0を初期値にする
  // （カート納品時の誤付与防止。詳細: docs/incidents/2026-08-09-都度利用コース変更後の入金確定処理による簡易チケット誤付与.md）
  ticket_number: 0,
  expire_days: 60,
  show_client_in_join: false,
  show_client_in_buy: false,
  only_for_buy: false,
  able_menu_ids: [],
  client_able_menu_ids: [],
  client_buyable_ticket_ids: [],
  overwrite_client_settings: false,
  reserve_type: 'stock_limit',
  monthly_stock_lesson_size: 0,
  limit_extend_for_monthy: 1,
  max_reserve_size: null,
  max_reserve_size_by_day: null,
  use_subscriptions: false,
  next_subsc_ticket_start_next_month: false,
  next_subsc_ticket_end_next_month: false,
  lesson_price: 0,
  lesson_term: 7,
  max_cancel_size: null,
  next_subsc_ticket_end_style: "now_to_limit",
  cancel_type: 'allterm',
  client_can_other_company: false,
  subsc_settings: {
    orders: [],
  },
  can_open_door: false,
  smartlock_reserve_required: null,
  auto_payment_enabled: false,
  prorate_enabled: false,
  start_date_rule: 'today_only',
}

// コース一覧API（/cmp/floor/courses index）は json.cache! でコレクション単位にキャッシュされるため、
// デプロイ直後は smartlock_reserve_required を含まない古いJSONが返りうる。
// キーが無いまま3択セレクト（null=店舗設定に従う / false=予約不要 / true=予約必須）に
// バインドすると空表示になるため、undefined のときだけ null へ寄せる。
// false（予約不要）は null と意味が異なるので絶対に変換しない。
export function normalizeSmartlockReserveRequired(obj) {
  if (!obj) return obj
  if (obj.smartlock_reserve_required === undefined) {
    obj.smartlock_reserve_required = null
  }
  return obj
}

// 解錠時の予約チェック（smartlock_reserve_required）の表示ラベル。
// null / undefined = 店舗設定に従う、false = 予約なしでも解錠できる、true = 予約した時間のみ解錠できる。
// undefined（smartlock_reserve_required を含まない古いキャッシュJSON）は null と同じ扱いにする。
// false（予約不要）は null と意味が異なるので、絶対に同じ扱いにしない。
export function smartlockReserveRequiredText(value) {
  if (value === true) return '予約した時間のみ解錠できる'
  if (value === false) return '予約なしでも解錠できる'
  return '店舗設定に従う'
}

// コース一覧など、一覧テーブルに併記する用の短いラベル。
// null / undefined（店舗設定に従う）のときは何も足さないので null を返す。
export function smartlockReserveRequiredShortText(value) {
  if (value === true) return '予約時のみ'
  if (value === false) return '予約不要'
  return null
}

// 入館ログ（smartlock_log）の action_type / status の和名。
// サーバの enum.ja.yml に smartlock_log の項目が無く、一覧が生値をそのまま出していたため
// フロント側に変換表を持つ。サーバ側で enum が増えても落ちないよう、未知の値は生値を返す。
const SMARTLOCK_LOG_ACTION_TYPE_TEXTS = {
  open_request: '解錠リクエスト',
  open: '解錠'
}

const SMARTLOCK_LOG_STATUS_TEXTS = {
  before: '処理待ち',
  success: '成功',
  error: '失敗',
  api_dead: 'API接続エラー',
  devise_dead: '機器接続エラー',
  battery_low: '電池残量低下'
}

export function smartlockLogActionTypeText(value) {
  if (!value) return '-'
  return SMARTLOCK_LOG_ACTION_TYPE_TEXTS[value] || value
}

export function smartlockLogStatusText(value) {
  if (!value) return '-'
  return SMARTLOCK_LOG_STATUS_TEXTS[value] || value
}

// チップの色。失敗（error / 接続エラー）だけを赤にして、一覧で失敗行を拾えるようにする
export function smartlockLogStatusVariant(value) {
  if (value === 'success') return 'success'
  if (value === 'error' || value === 'api_dead' || value === 'devise_dead')
    return 'danger'
  if (value === 'battery_low') return 'warning'
  return 'secondary'
}

// 一覧のコントローラが action_type を open_request / open に絞っているので、
// 選択肢もその2つだけにする（smartlock_logs_controller.rb の where(action_type:) と対応）
export const SMARTLOCK_LOG_ACTION_TYPE_OPTIONS = [
  'open_request',
  'open'
].map(value => ({ value, text: smartlockLogActionTypeText(value) }))

export const SMARTLOCK_LOG_STATUS_OPTIONS = [
  'before',
  'success',
  'error',
  'api_dead',
  'devise_dead',
  'battery_low'
].map(value => ({ value, text: smartlockLogStatusText(value) }))

export let time_zone = {
  company_group_id: null,
  sort: 1,
  name: '',
  name_inner: '',
  can_0: false,
  can_1: false,
  can_2: false,
  can_3: false,
  can_4: false,
  can_5: false,
  can_6: false,
  can_7: false,
  can_8: false,
  can_9: false,
  can_10: false,
  can_11: false,
  can_12: false,
  can_13: false,
  can_14: false,
  can_15: false,
  can_16: false,
  can_17: false,
  can_18: false,
  can_19: false,
  can_20: false,
  can_21: false,
  can_22: false,
  can_23: false,
  settings: {
    lists:   [
      {
        wday: 0,
        name: "日曜",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },
      {
        wday: 1,
        name: "月曜",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },
      {
        wday: 2,
        name: "火曜",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },    
      {
        wday: 3,
        name: "水曜",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },    
      {
        wday: 4,
        name: "木曜",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },    
      {
        wday: 5,
        name: "金曜",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },    
      {
        wday: 6,
        name: "土曜",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },
      {
        wday: 7,
        name: "祝日",
        can_0: false,
        can_1: false,
        can_2: false,
        can_3: false,
        can_4: false,
        can_5: false,
        can_6: false,
        can_7: false,
        can_8: false,
        can_9: false,
        can_10: false,
        can_11: false,
        can_12: false,
        can_13: false,
        can_14: false,
        can_15: false,
        can_16: false,
        can_17: false,
        can_18: false,
        can_19: false,
        can_20: false,
        can_21: false,
        can_22: false,
        can_23: false,    
      },
    ]
  }
}

export let survey = {
  company_group_id: null,
  status: 'active',
  name: '',
  name_inner: '',
  description: '',
  use_scene: null,
  only_onece: true,
  start: null,
  end: null,
  survey_questions: [],
  disactive_survey_questions: []
}

export let survey_question = {
  id: null,
  sort: 1,
  title: '',
  description: '',
  style: 'text',
  required: true,
  choices: [
    {
      value: ''
    }
  ]
}

export let task = {
  title: '',
  limit: null,
  memo: '',
  docs: [],
  doc_ids: [],
}

export let item_category = {
  company_group_id: null,
  sort: 1,
  name: '',
  color: '',
  show_client: true
}

export let item = {
  company_group_id: null,
  sort: 1,
  name_inner: '',
  name: '',
  sku: "",
  for_company: false,
  description: '',
  price: 0,
  tax_rate: 10,
  manage_stock: false,
  stock_size: 0,
  show_client: true,
  is_subscription: false,
  item_category_id: null,
  section_id: null
}

export let section = {
  sort: 1,
  name: '',
  color: ''
}

export let discount = {
  name: '',
  discount_amount: 0,
  duration_type: 'months',
  duration_months: null,
  selectable_by_client: false,
  coupon_code: null,
  is_active: true,
  expires_at: null,
}

export let durationTypeOptions = [
  { text: '期間限定（月数指定）', value: 'months' },
  { text: '永続', value: 'permanent' },
]

export let payment_method = {
  company_group_id: null,
  sort: 1,
  name: '',
  color: '',
  is_cash: false,
  is_for_urikake: false,
  ticket_add_type: "at_payment"
}

export let ticket_set = {
  name: '',
  description: '',
  price: 0,
  ticket_number: 1,
  stock_number: 1,
  use_from: dayjs().format('YYYY-MM-DD'),
  use_to: dayjs().add(365, 'd').format('YYYY-MM-DD'),
  expire_days: 365
}

export let goal_term = {
  start_staff_id: null,
  end_staff_id: null,
  begining_lesson_id: null,
  end_lesson_id: null,
  begining_bodyscan_id: null,
  end_bodyscan_id: null,
  start_front_img: null,
  start_side_img: null,
  start_back_img: null,
  end_front_img: null,
  end_side_img: null,
  end_back_img: null,
  title: null,
  memo: null,
  start_comment: null,
  end_comment: null,
  start_date: null,
  goal_date: null,
  datas: {
    start: {
      weight: null,
      fat_rate: null,
      bmi: null,
      neck_girth_length: null,
      upper_arm_girth_length: null,
      bust_girth_length: null,
      waist_girth_length: null,
      hip_girth_length: null,
      thigh_girth_length: null,
      max_weight_deadlift: null,
      max_weight_squat: null,
      max_weight_bench_press: null,
    },
    end: {
      weight: null,
      fat_rate: null,
      bmi: null,
      neck_girth_length: null,
      upper_arm_girth_length: null,
      bust_girth_length: null,
      waist_girth_length: null,
      hip_girth_length: null,
      thigh_girth_length: null,
      max_weight_deadlift: null,
      max_weight_squat: null,
      max_weight_bench_press: null,
    },
    goal: {
      weight: null,
      fat_rate: null,
      bmi: null,
      neck_girth_length: null,
      upper_arm_girth_length: null,
      bust_girth_length: null,
      waist_girth_length: null,
      hip_girth_length: null,
      thigh_girth_length: null,
      max_weight_deadlift: null,
      max_weight_squat: null,
      max_weight_bench_press: null,
    },
  }
}

export let training_category = {
  company_group_id: null,
  name: null,
  name_kana: null,
  show_client: true
}

export let training_event = {
  company_group_id: null,
  training_category_id: null,
  sort: 1,
  name: null,
  name_kana: null,
  show_client: true,
  media: {
    doc_ids: [],
    video_ids: []
  },
  img_icon: null,
  headline: null,
  target_muscles: [],
  machine: [],
  pof_type: null,
  difficulty: null,
  guide_memo: null,
  inner_memo: null,
  weight_measure: '重量',
  count_measure: '回数',
  weight_unit: 'kg',
  count_unit: '回',
  level: null
}

export let content_category = {
  name: null,
  position: 1
}

export let content = {
  content_category_id: null,
  company_id: null,
  sort: 1,
  title: null,
  description: null,
  tags: [],
  thumbnail: null,
  body_blocks: [],
  media: {
    doc_ids: [],
    video_ids: []
  },
  show_client: false,
  hide_from_list: false,
  published_at: null,
  docs: [],
  videos: []
}

export let content_sequence = {
  name: null,
  trigger: 'signup',
  is_active: false,
  stop_on_convert: true,
  company_id: null,
  target_company_ids: [],
  target_course_ids: [],
  steps: []
}

export let content_sequence_step = {
  content_id: null,
  delay_days: 0,
  send_alert: true,
  send_email: true,
  send_push: true,
  email_mode: 'link',
  position: 1
}

// 種目のトレ履歴
export let training_set = {
  weight: null,
  count: 10,
  is_giri: 0,
  is_support: 0,
  support_count: null,
  memo: null,
  event: {
    weight_unit: 'kg',
    count_unit: '回'
  }
}

export let training_list = {
  company_group_id: null,
  training_category_id: null,
  uid: null,
  sort: 1,
  bookmark: false,
  name: '',
  description: '',
  target_muscles: [],
  machine: [],
  pof_type: null,
  difficulty: null,
  training_events: {
    event_sets: []
  }
}

export let training_muscle = {
  company_group_id: null,
  uid: null,
  status: null,
  sort: 1,
  img_icon: null,
  name: '',
  description: '',
  show_client: false,
}

export let training_machine = {
  company_group_id: null,
  uid: null,
  status: null,
  sort: 1,
  img_icon: null,
  name: '',
  description: '',
  show_client: false,
}

// インストラクター（本部マスタ。イベントの担当講師。表示専用）
// 設計書: docs/plans/2026-10-20-event-instructor-and-one-off-event-design.md
// company_ids が空＝全店舗で使える。staff_id は予約表の「自分の担当」にだけ使う
export let instructor = {
  uid: null,
  name: '',
  img_icon: null,
  profile: '',
  staff_id: null,
  company_ids: [],
}

export let reserve_setting = {
  name: '',
  max_is_common: false,
  max_reserve_size: null,
  max_reserve_size_by_day: null,
  cli_calendar_visible_date_from: null,
  reserve_explain: null,
  attendance_setting: {
    can_sunday: true,
    can_monday: true,
    can_thuseday: true,
    can_wedensday: true,
    can_thursday: true,
    can_friday: true,
    can_saturday: true,
    can_holiday: true,
    from_on_sunday: "09:00",
    from_on_monday: "09:00",
    from_on_thuseday: "09:00",
    from_on_wedensday: "09:00",
    from_on_thursday: "09:00",
    from_on_friday: "09:00",
    from_on_saturday: "09:00",  
    from_on_holiday: "09:00",  
    to_on_sunday: "22:00",
    to_on_monday: "22:00",
    to_on_thuseday: "22:00",
    to_on_wedensday: "22:00",
    to_on_thursday: "22:00",
    to_on_friday: "22:00",
    to_on_saturday: "22:00",
    to_on_holiday: "22:00",
  },
  use_attendance_setting: false,
  cli_calendar_visible_day_size: null,
  client_reserved_mail_template: null,
  // null = 未設定（メニュー・店舗の設定にフォールバックする）
  // 既定値を入れると「触っていない項目まで上書きされる」事故が起きるため空にする
  reserve_cancele_limit_hour: null,
  reserve_cancele_limit_day: null,
  reserve_from_to_hour: null,
  reserve_from_to: null,
  reserve_from_from: null,
  next_month_can_reserve_from: null,
  can_reserve_from_date: null,
  use_reserve_from_to_cancel_rule: true, // 判定には使わない（空欄=店舗設定）。列削除まで新規はONで保存する
  show_resource: false,
}

export let company_group = {
  name: '',
  cmp_ids: [],
}

// 個人のマイレコ
export let client_training_event = {
  uid: null,
  status: null,
  favarite_type: '',
  doc_ids: '',
  memo: null,
  inner_memo: null,
  max_weight: null,
  max_sum_weight: null,
  count_at_gym: null,
  count_at_self: null,
  latest_at_gym: null,
  latest_by_self: null,
  histories: []
}

// セッションの記録
export let custom_lesson_attrs = {
  uid: null,
  name: null,
  type: 'kg',
  options: '',
  required: false,
  show: true,
  chart: false,
  color: 'black',
  deleted_at: null
}

// 日々のライフログ
export let custom_client_trend_attrs = {
  uid: null,
  name: null,
  type: 'text',
  options: '',
  required: false,
  show: true,
  chart: false,
  color: 'black',
  deleted_at: null
}

export let custom_lesson_options = [
  { text: 'cm', value: 'cm' },
  { text: 'kg', value: 'kg' },
  { text: '数字', value: 'number' }
]

export let custom_client_trend_options = [
  { text: '文字列', value: 'text' },
  { text: 'テキストエリア', value: 'textarea' },
  { text: '数字', value: 'number' },
  { text: 'kg', value: 'kg' },
  { text: '%', value: 'rate' },
  { text: '選択', value: 'radio' },
  { text: '複数選択', value: 'checkbox' },
  { text: '画像', value: 'image' },
  { text: 'ファイル', value: 'file' }
]

///// status --------------------

export let training_event_status = [
  { text: '未設定', value: 'never' },
  { text: '苦手', value: 'not_good' },
  { text: '上手', value: 'good' },
  { text: '非常に上手', value: 'very_good' },
  { text: '完了', value: 'done' }
]

export let clientStatusOptions = [
  { text: 'ビジター', value: 'trial' },
  { text: '会員', value: 'active' },
  { text: '停止中', value: 'disactive' }
]

export let subscTypeOptions = [
  { text: '1日払い', value: 'pay_at_first' },
  { text: '購入日指定', value: 'pay_at_date' },
  { text: '定期期間払い', value: 'pay_with_term' }
]

export let planTypeOptions = [
  { value: null, text: '選んでください' },
  { value: 'unlimit', text: '無制限' },
  { value: 'stock', text: 'チケット(簡易)' },
  { value: 'stock_limit', text: 'チケット(厳密)' },
  { value: 'monthly_stock', text: '月回数制限' },
  { value: 'weekly_limit', text: '週回数制限' }
]

export let sexOptions = [
  { value: null, text: '選んでください' },
  { value: 'male', text: '男性' },
  { value: 'female', text: '女性' },
  { value: 'other', text: '選択しない' },
]

export let activity_level_options = [
  { text: '低い', rate: 1.2, value: 'low', description: 'デスクワーク中心で、ほとんど運動しない日常。週に1-2回軽い散歩程度。' },
  { text: '普通', rate: 1.375, value: 'normal', description: 'デスクワーク中心だが、週に1-3回軽い運動（ウォーキング、軽いヨガなど）を行う。' },
  { text: 'やや活動的', rate: 1.55, value: 'high', description: '週に3-5回、中強度の運動（ジョギング、筋トレ、スポーツなど）を行う。立ち仕事も含む。' },
  { text: '活動的', rate: 1.725, value: 'very_high', description: '週に6-7回、激しい運動や長時間の運動を行う。肉体労働者やアスリートレベル。' },
]

export let pfc_pattern_options = [
  { text: 'バランス型（4:3:3）', value: 'balance', p_rate: 30, f_rate: 30, c_rate: 40, description: '最も持続しやすく、初心者におすすめ。筋肉量を維持しながら緩やかに減量。' },
  { text: '高タンパク質型（4:2:4）', value: 'highprotein', p_rate: 40, f_rate: 20, c_rate: 40, description: '筋トレをしている人や、筋肉量を重視する場合。満腹感も得やすい。' },
  { text: '低炭水化物型（ケトジェニック）', value: 'keto', p_rate: 25, f_rate: 70, c_rate: 5, description: '糖質を極端に制限し、脂質をエネルギー源とする。短期間で効果が出やすいが、継続が困難。' },
  { text: '中程度低炭水化物型', value: 'moderatelowcarb', p_rate: 30, f_rate: 40, c_rate: 30, description: 'ケトほど極端ではないが、炭水化物を控えめにした現実的なアプローチ。' },
  { text: '低脂質型', value: 'lowfat', p_rate: 30, f_rate: 15, c_rate: 55, description: '脂質を最小限に抑える従来型。米や麺類が好きな人に向いているが、ホルモンバランスに注意が必要。' },
  { text: 'リーンゲインズ型', value: 'leangains', p_rate: 37, f_rate: 27, c_rate: 36, description: '間欠的断食と組み合わせることが多い。トレーニング日と休息日でカロリーを変動させる。' },
]

export let listExtendForMonthlyOptions = [
  { text: '当月中のみ', value: 1 },
  { text: '2ヶ月有効', value: 2 }
  // { text: '3ヶ月有効', value: 3 },
]

export let reserveUnitOptions = [
  { value: null, text: '選んでください' },
  { value: 5, text: '5分' },
  { value: 10, text: '10分' },
  { value: 15, text: '15分' },
  { value: 20, text: '20分' },
  { value: 25, text: '25分' },
  { value: 30, text: '30分' },
  { value: 35, text: '35分' },
  { value: 40, text: '40分' },
  { value: 45, text: '45分' },
  { value: 50, text: '50分' },
  { value: 55, text: '55分' },
  { value: 60, text: '60分' },
  { value: 65, text: '65分' },
  { value: 70, text: '70分' },
  { value: 75, text: '75分' },
  { value: 80, text: '80分' },
  { value: 85, text: '85分' },
  { value: 90, text: '90分' },
  { value: 95, text: '95分' },
  { value: 100, text: '100分' },
  { value: 105, text: '105分' },
  { value: 110, text: '110分' },
  { value: 115, text: '115分' },
  { value: 120, text: '120分' },
  { value: 125, text: '125分' },
  { value: 130, text: '130分' },
  { value: 135, text: '135分' },
  { value: 140, text: '140分' },
  { value: 145, text: '145分' },
  { value: 150, text: '150分' },
  { value: 155, text: '155分' },
  { value: 160, text: '160分' },
  { value: 165, text: '165分' },
  { value: 170, text: '170分' },
  { value: 175, text: '175分' },
  { value: 180, text: '180分' }
]

export let unitOptions = [
  { value: null, text: '選んでください' },
  { value: 5, text: '5分' },
  { value: 10, text: '10分' },
  { value: 15, text: '15分' },
  { value: 30, text: '30分' },
  { value: 60, text: '60分' }
]

export let taxRateOptions = [
  { text: '10%', value: 10 },
  { text: '8%', value: 8 },
  { text: '0%', value: 0 },
]

export let lessonTypeOptions = [
  { id: 'lesson', name: 'セッション' },
  { id: 'block_calendar', name: '予約先' },
  { id: 'block_room', name: '共有設備' },
  { id: 'block_with_shop', name: '店舗' },
  { id: 'event', name: 'イベント' }
]

export let tableModeStatus = [
  { text: 'テーブル', value: 'table' },
  { text: 'カード', value: 'card' }
]

export let eventHeadlines = [
  { type: 'all', label: '全て' },
  { type: 'ア', label: 'ア', active: true },
  { type: 'イ', label: 'イ', active: true },
  { type: 'ウ', label: 'ウ', active: true },
  { type: 'エ', label: 'エ', active: true },
  { type: 'オ', label: 'オ', active: true },
  { type: 'カ', label: 'カ', active: true },
  { type: 'キ', label: 'キ', active: true },
  { type: 'ク', label: 'ク', active: true },
  { type: 'ケ', label: 'ケ', active: true },
  { type: 'コ', label: 'コ', active: true },
  { type: 'サ', label: 'サ', active: true },
  { type: 'シ', label: 'シ', active: true },
  { type: 'ス', label: 'ス', active: true },
  { type: 'セ', label: 'セ', active: true },
  { type: 'ソ', label: 'ソ', active: true },
  { type: 'タ', label: 'タ', active: true },
  { type: 'チ', label: 'チ', active: true },
  { type: 'ツ', label: 'ツ', active: true },
  { type: 'テ', label: 'テ', active: true },
  { type: 'ト', label: 'ト', active: true },
  { type: 'ナ', label: 'ナ', active: true },
  { type: 'ニ', label: 'ニ', active: true },
  { type: 'ヌ', label: 'ヌ', active: true },
  { type: 'ネ', label: 'ネ', active: true },
  { type: 'ノ', label: 'ノ', active: true },
  { type: 'ハ', label: 'ハ', active: true },
  { type: 'ヒ', label: 'ヒ', active: true },
  { type: 'フ', label: 'フ', active: true },
  { type: 'ヘ', label: 'ヘ', active: true },
  { type: 'ホ', label: 'ホ', active: true },
  { type: 'マ', label: 'マ', active: true },
  { type: 'ミ', label: 'ミ', active: true },
  { type: 'ム', label: 'ム', active: true },
  { type: 'メ', label: 'メ', active: true },
  { type: 'モ', label: 'モ', active: true },
  { type: 'ヤ', label: 'ヤ', active: true },
  { type: 'ユ', label: 'ユ', active: true },
  { type: 'ヨ', label: 'ヨ', active: true },
  { type: 'ラ', label: 'ラ', active: true },
  { type: 'リ', label: 'リ', active: true },
  { type: 'ル', label: 'ル', active: true },
  { type: 'レ', label: 'レ', active: true },
  { type: 'ロ', label: 'ロ', active: true },
  { type: 'ワ', label: 'ワ', active: true },
  { type: 'other', label: 'その他', active: true }
]

export let surveySceneOptions = [
  // {value: "client_create", text: "新規会員登録時"},
  { value: 'lesson_trial', text: '体験予約時', disabled: false },
  { value: 'lesson_reserve', text: '予約時', disabled: false },
  { value: 'client_join', text: '入会時', disabled: false },
  // {value: "course_change", text: "コース変更時"},
  // {value: "client_destroy", text: "退会時"},
  { value: 'free', text: 'フリー', disabled: true }
]

export let questionStyleOptions = [
  { value: 'text', text: 'テキスト(1行)' },
  { value: 'textarea', text: 'テキスト(複数行)' },
  { value: 'radio', text: 'ラジオボタン(1つ選択)' },
  { value: 'checkbox', text: 'チェックボックス(複数選択)' },
  { value: 'date', text: '日付' }
]

export let taskStatusOptions = [
  { text: '対応中', value: 'inprogress' },
  { text: '完了', value: 'done' },
  { text: '削除', value: 'pending' }
]

export let statusOptions = [
  { text: '有効', value: 'active' },
  { text: '無効', value: 'disactive' }
]

export let nextSubscTicketEndStyles = [
  { text: '付与日から有効日数', value: 'now_to_limit' },
  { text: '付与日から翌月末まで', value: 'now_to_nextm' },
  { text: '付与日から翌々月末まで', value: 'now_to_next2m' },
  { text: '次月初から有効日数', value: 'nextm_to_limit' },
  { text: '次月初から翌末まで（1ヶ月間）', value: 'nextm_all' },
  { text: '次月初から翌々末まで（2ヶ月間）', value: 'nextm_to_next2m' },
  { text: '翌々月初から有効日数', value: 'next2m_to_limit' },
  { text: '翌々月初から翌々月末まで（1ヶ月間）', value: 'next2m_all' },
  { text: '翌々月初から3ヶ月後末まで（2ヶ月間）', value: 'next2m_to_next3m' },
]

export let cancelTypes = [
  { text: '全期間', value: 'allterm' },
  { text: '月次', value: 'monthly' },
]

export let visitorSiteTypes = [
  { text: 'フォーム > カレンダー', value: 'form_calendar' },
  { text: 'カレンダー > フォーム', value: 'calendar_form' },
  { text: '問い合わせフォームのみ', value: 'onlyform' },
]

export let ticketAddTypeOptions = [
  { text: '決済日登録時', value: 'at_payment' },
  { text: 'チケット付与日', value: 'at_exec' },
  // { text: '入金処理時', value: 'at_collect' },
]

export let model_types = [
  { key: 'flash-lite', label: '高速', description: '処理が早いが精度は標準' },
  { key: 'flash', label: '標準', description: 'バランスの取れた精度と速度' },
  { key: 'pro', label: '高精度', description: '最高精度だが処理に時間がかかる' },
]